import { Fragment } from 'react';

import { Outlet } from 'react-router';

import { cn, Logger, LogLevel } from '@repo/utils/common';

import { i18n } from '@/shared/configs/i18n.config';
import { CONTENT_OFFSET_CLASS } from '@/shared/configs/toolbar.config';
import { cartContext } from '@/shared/middlewares/cartContext';
import { userContext } from '@/shared/middlewares/userContext';
import { getLocaleFromRequest, validateLocaleOrRedirect } from '@/shared/utils/i18n.utils';
import type { Route } from '@/router-types/src/+types/root';

import { getCart } from '@/client/services/cart.service';
import { getUser } from '@/client/services/user.service';

import { Footer } from '@/components/common/Footer';
import { RootHydrateFallback } from '@/components/fallback/RootHydrateFallback';
import { RootLayout } from '@/components/layouts/RootLayout';
import { RootErrorBoundary } from '@/components/RootErrorBoundary';
import { Toolbar } from '@/components/toolbar/Toolbar';

import '@/shared/configs/i18n.config';
import './fonts/fonts.css';
import './globals.css';

export const Layout = RootLayout;
export const ErrorBoundary = RootErrorBoundary;
export const HydrateFallback = RootHydrateFallback;

const logger = new Logger(LogLevel.DEBUG);

export const clientMiddleware: Route.ClientMiddlewareFunction[] = [
  async ({ request, context }, next) => {
    const locale = getLocaleFromRequest(request);
    const cartPromise = getCart(locale)
      .then((cart) => ({ status: 'ok' as const, cart }))
      .catch((error: unknown) => {
        logger.error('Cart hydration failed', error);
        return { status: 'error' as const, error };
      });
    const { user } = await getUser();

    context.set(userContext, user);
    context.set(cartContext, cartPromise);

    return next();
  },
];

export const clientLoader = async ({ request, params, context }: Route.ClientLoaderArgs) => {
  const url = new URL(request.url);
  const locale = validateLocaleOrRedirect(url, params.locale);

  if (i18n.language !== locale) {
    try {
      await i18n.changeLanguage(locale);
    } catch (error) {
      logger.error('Failed to change language', error);
    }
  }

  return {
    locale,
    user: context.get(userContext),
    cart: context.get(cartContext),
  };
};

export const meta: Route.MetaFunction = () => [
  {
    title: 'SIP Konfigurator',
  },
];

const App = () => (
  <Fragment>
    <Toolbar />
    <div className={cn('flex flex-1 flex-col', CONTENT_OFFSET_CLASS)}>
      <Outlet />
    </div>
    <Footer />
  </Fragment>
);

export default App;
